import { z } from 'zod';
import { FileSummarySchema, PersonRefSchema, SiteRefSchema } from './property';

/**
 * HR lifecycle (docs/specs/hr-lifecycle.md, D74): approval flows, the shared checklist engine,
 * hires, variations (dated employment terms), leavers, probation reviews with drawn signatures,
 * right to work, cases, training and statutory-leave paperwork.
 *
 * Pay is a separate, optional `pay` object that the API fills only for pay viewers (and audits
 * each time); everyone else gets `pay: null` with `payHidden: true`. Lists never carry pay.
 */

const IsoDate = z.iso.date();
const Instant = z.iso.datetime();
const Text = (max: number) => z.string().trim().max(max);
const OptText = (max: number) => Text(max).nullish();

export const HrFlowSchema = z.enum(['hire', 'variation', 'leaver_replacement']);
export type HrFlow = z.infer<typeof HrFlowSchema>;
export const HR_FLOW_LABELS: Record<HrFlow, string> = {
  hire: 'New hire',
  variation: 'Contract variation',
  leaver_replacement: 'Replace a leaver',
};

export const PayTypeSchema = z.enum(['hourly', 'salary']);
export type PayType = z.infer<typeof PayTypeSchema>;
export const ContractTypeSchema = z.enum(['permanent', 'fixed_term', 'casual']);
export type ContractType = z.infer<typeof ContractTypeSchema>;
export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  permanent: 'Permanent',
  fixed_term: 'Fixed term',
  casual: 'Casual / zero hours',
};
export const RagSchema = z.enum(['red', 'amber', 'green']);
export type Rag = z.infer<typeof RagSchema>;

/** Pay figures; only ever sent to pay viewers. */
export const PaySchema = z.object({
  payType: PayTypeSchema.nullable(),
  hourlyRate: z.number().nullable(),
  annualSalary: z.number().nullable(),
});
export type Pay = z.infer<typeof PaySchema>;

export const PayWriteSchema = z
  .object({
    payType: PayTypeSchema,
    hourlyRate: z.number().positive().max(1000).nullish(),
    annualSalary: z.number().positive().max(1_000_000).nullish(),
  })
  .refine((p) => (p.payType === 'hourly' ? p.hourlyRate != null : p.annualSalary != null), {
    message: 'Give the hourly rate or the annual salary for the pay type.',
  });
export type PayWrite = z.infer<typeof PayWriteSchema>;

export const HrOkSchema = z.object({ ok: z.literal(true) });

// Approval flows (settings) ---------------------------------------------------------------

export const ApprovalMemberKindSchema = z.enum(['person', 'seat', 'role']);
export const ApprovalMemberSchema = z.object({
  kind: ApprovalMemberKindSchema,
  id: z.uuid(),
  label: z.string(),
  /** Who that seat / role resolves to today (people: themselves). */
  holders: z.array(PersonRefSchema),
});
export type ApprovalMember = z.infer<typeof ApprovalMemberSchema>;

export const ApprovalModeSchema = z.enum(['any', 'all']);
export const ApprovalStepSchema = z.object({
  stepNo: z.number().int(),
  name: z.string(),
  mode: ApprovalModeSchema,
  members: z.array(ApprovalMemberSchema),
});
export type ApprovalStep = z.infer<typeof ApprovalStepSchema>;

export const ApprovalFlowSchema = z.object({
  flow: HrFlowSchema,
  name: z.string(),
  steps: z.array(ApprovalStepSchema),
  updatedAt: Instant,
  updatedBy: PersonRefSchema.nullable(),
});
export type ApprovalFlow = z.infer<typeof ApprovalFlowSchema>;

export const ApprovalFlowsResponseSchema = z.object({
  flows: z.array(ApprovalFlowSchema),
  options: z.object({
    seats: z.array(
      z.object({
        id: z.uuid(),
        code: z.string(),
        title: z.string(),
        holder: PersonRefSchema.nullable(),
      }),
    ),
    roles: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string() })),
  }),
});
export type ApprovalFlowsResponse = z.infer<typeof ApprovalFlowsResponseSchema>;

export const ApprovalFlowWriteSchema = z.object({
  name: Text(100).min(1),
  steps: z
    .array(
      z.object({
        name: Text(100).min(1),
        mode: ApprovalModeSchema,
        members: z
          .array(z.object({ kind: ApprovalMemberKindSchema, id: z.uuid() }))
          .min(1)
          .max(20),
      }),
    )
    .min(1)
    .max(6),
});
export type ApprovalFlowWrite = z.infer<typeof ApprovalFlowWriteSchema>;

/** Where a request is in its approval flow. */
export const ApprovalProgressSchema = z.object({
  status: z.enum(['pending', 'approved', 'rejected']),
  currentStep: z.number().int().nullable(),
  currentStepName: z.string().nullable(),
  steps: z.array(
    z.object({
      stepNo: z.number().int(),
      name: z.string(),
      mode: ApprovalModeSchema,
      members: z.array(z.string()),
      state: z.enum(['waiting', 'current', 'approved', 'rejected']),
      decisions: z.array(
        z.object({
          person: PersonRefSchema.nullable(),
          decision: z.enum(['approve', 'reject']),
          note: z.string().nullable(),
          at: Instant,
        }),
      ),
      /** People who may decide this step now (current step only). */
      approvers: z.array(PersonRefSchema),
    }),
  ),
  /** The viewer may approve or reject the current step. */
  canDecide: z.boolean(),
});
export type ApprovalProgress = z.infer<typeof ApprovalProgressSchema>;

export const DecisionWriteSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  note: OptText(1000),
});
export type DecisionWrite = z.infer<typeof DecisionWriteSchema>;

// Checklists ---------------------------------------------------------------------------------

export const ChecklistFlowSchema = z.enum(['hire', 'induction', 'variation', 'leaver']);
export type ChecklistFlow = z.infer<typeof ChecklistFlowSchema>;
export const OwnerKindSchema = z.enum(['hr', 'site_manager', 'rm', 'person', 'it']);
export type OwnerKind = z.infer<typeof OwnerKindSchema>;
export const OWNER_KIND_LABELS: Record<OwnerKind, string> = {
  hr: 'HR',
  site_manager: 'Branch manager',
  rm: 'Regional manager',
  person: 'The employee',
  it: 'IT',
};
export const EvidenceSchema = z.enum(['tick', 'date', 'file', 'signature']);
export type Evidence = z.infer<typeof EvidenceSchema>;
export const AnchorSchema = z.enum(['start', 'created', 'leave', 'effective']);

export const HrTaskSchema = z.object({
  id: z.uuid(),
  category: z.string(),
  subcategory: z.string().nullable(),
  title: z.string(),
  ownerKind: OwnerKindSchema,
  ownerLabel: z.string(),
  dueOn: IsoDate,
  evidence: EvidenceSchema,
  day1: z.boolean(),
  status: z.enum(['open', 'done', 'na']),
  doneBy: PersonRefSchema.nullable(),
  doneAt: Instant.nullable(),
  note: z.string().nullable(),
  valueDate: IsoDate.nullable(),
  file: FileSummarySchema.nullable(),
  source: z.enum(['aroo', 'import', 'readback']),
  overdue: z.boolean(),
  canAct: z.boolean(),
});
export type HrTask = z.infer<typeof HrTaskSchema>;

export const ChecklistSchema = z.object({
  id: z.uuid(),
  flow: ChecklistFlowSchema,
  name: z.string(),
  anchorDate: IsoDate,
  status: z.enum(['open', 'done', 'cancelled']),
  done: z.number().int(),
  total: z.number().int(),
  tasks: z.array(HrTaskSchema),
});
export type Checklist = z.infer<typeof ChecklistSchema>;

export const TaskUpdateSchema = z.object({
  status: z.enum(['open', 'done', 'na']),
  note: OptText(1000),
  valueDate: IsoDate.nullish(),
});
export type TaskUpdate = z.infer<typeof TaskUpdateSchema>;

export const MyHrTasksResponseSchema = z.object({
  tasks: z.array(
    HrTaskSchema.extend({
      checklistId: z.uuid(),
      checklistName: z.string(),
      flow: ChecklistFlowSchema,
      person: PersonRefSchema.nullable(),
      link: z.string(),
    }),
  ),
});
export type MyHrTasksResponse = z.infer<typeof MyHrTasksResponseSchema>;

/** HR › My tasks: everything the daily digest would tell the viewer, live. */
export const MyHrSchema = z.object({
  tasks: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      checklistName: z.string(),
      person: PersonRefSchema.nullable(),
      dueOn: IsoDate,
      overdue: z.boolean(),
      link: z.string(),
    }),
  ),
  approvals: z.array(
    z.object({
      kind: z.enum(['hire', 'variation', 'leaver_replacement']),
      id: z.uuid(),
      title: z.string(),
      link: z.string(),
    }),
  ),
  reviews: z.array(
    z.object({
      id: z.uuid(),
      person: PersonRefSchema,
      title: z.string(),
      dueOn: IsoDate,
      overdue: z.boolean(),
    }),
  ),
  probations: z.array(z.object({ person: PersonRefSchema, endsOn: IsoDate })),
  rtw: z.array(z.object({ person: PersonRefSchema, expiresOn: IsoDate })),
  training: z.array(z.object({ person: PersonRefSchema, course: z.string(), expiresOn: IsoDate })),
  hiresToComplete: z.number().int(),
});
export type MyHr = z.infer<typeof MyHrSchema>;

// Documents and signatures --------------------------------------------------------------------

export const SignerRoleSchema = z.enum(['manager', 'employee', 'hr']);
export type SignerRole = z.infer<typeof SignerRoleSchema>;
export const SIGNER_ROLE_LABELS: Record<SignerRole, string> = {
  manager: 'Manager',
  employee: 'Employee',
  hr: 'HR',
};
export const HrDocumentKindSchema = z.enum([
  'variation_letter',
  'probation_review',
  'leaver_form',
  'case_letter',
  'statutory',
  'other',
]);
export type HrDocumentKind = z.infer<typeof HrDocumentKindSchema>;

export const HrDocumentSchema = z.object({
  id: z.uuid(),
  person: PersonRefSchema,
  kind: HrDocumentKindSchema,
  title: z.string(),
  status: z.enum(['draft', 'awaiting', 'signed']),
  requiredSigners: z.array(SignerRoleSchema),
  signatures: z.array(
    z.object({ role: SignerRoleSchema, signerName: z.string(), signedAt: Instant }),
  ),
  signedFile: FileSummarySchema.nullable(),
  createdAt: Instant,
  /** Roles the viewer may sign for now. */
  canSign: z.array(SignerRoleSchema),
});
export type HrDocument = z.infer<typeof HrDocumentSchema>;

export const SignatureWriteSchema = z.object({
  role: SignerRoleSchema,
  /** `data:image/png;base64,...` from the canvas. */
  png: z
    .string()
    .max(600_000)
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/),
});
export type SignatureWrite = z.infer<typeof SignatureWriteSchema>;

// Employment terms ----------------------------------------------------------------------------

export const TermsViewSchema = z.object({
  id: z.uuid(),
  effectiveFrom: IsoDate,
  jobTitle: z.string().nullable(),
  jobRole: z.string().nullable(),
  site: SiteRefSchema.nullable(),
  contractHours: z.number().nullable(),
  contractType: ContractTypeSchema.nullable(),
  holidayEntitlementDays: z.number().nullable(),
  source: z.enum(['hire', 'variation', 'import', 'manual']),
  current: z.boolean(),
  future: z.boolean(),
  pay: PaySchema.nullable(),
});
export type TermsView = z.infer<typeof TermsViewSchema>;

export const ApprovalBriefSchema = z.object({
  status: z.enum(['pending', 'approved', 'rejected']),
  currentStepName: z.string().nullable(),
  waitingOnMe: z.boolean(),
});

// Variations ----------------------------------------------------------------------------------

export const VariationStatusSchema = z.enum([
  'pending',
  'approved',
  'applied',
  'rejected',
  'cancelled',
]);
export type VariationStatus = z.infer<typeof VariationStatusSchema>;

export const VariationSummarySchema = z.object({
  id: z.uuid(),
  ref: z.string(),
  person: PersonRefSchema,
  site: SiteRefSchema.nullable(),
  status: VariationStatusSchema,
  effectiveFrom: IsoDate,
  changes: z.array(z.string()),
  raisedBy: PersonRefSchema.nullable(),
  raisedAt: Instant,
  approval: ApprovalBriefSchema,
});
export type VariationSummary = z.infer<typeof VariationSummarySchema>;

export const VariationDetailSchema = VariationSummarySchema.extend({
  reason: z.string().nullable(),
  current: TermsViewSchema.nullable(),
  proposed: z.object({
    jobTitle: z.string().nullable(),
    jobRole: z.string().nullable(),
    site: SiteRefSchema.nullable(),
    contractHours: z.number().nullable(),
    payChanged: z.boolean(),
    pay: PaySchema.nullable(),
  }),
  payHidden: z.boolean(),
  approvals: ApprovalProgressSchema,
  letter: HrDocumentSchema.nullable(),
  can: z.object({ cancel: z.boolean(), createLetter: z.boolean() }),
});
export type VariationDetail = z.infer<typeof VariationDetailSchema>;

export const VariationCreateSchema = z
  .object({
    personId: z.uuid(),
    effectiveFrom: IsoDate,
    jobTitle: OptText(100),
    jobRole: z.string().max(40).nullish(),
    siteId: z.uuid().nullish(),
    contractHours: z.number().min(0).max(80).nullish(),
    pay: PayWriteSchema.nullish(),
    reason: OptText(1000),
  })
  .refine(
    (v) => !!v.jobTitle || !!v.jobRole || !!v.siteId || v.contractHours != null || v.pay != null,
    { message: 'Change at least one term.' },
  );
export type VariationCreate = z.infer<typeof VariationCreateSchema>;

export const VariationsResponseSchema = z.object({ variations: z.array(VariationSummarySchema) });
export type VariationsResponse = z.infer<typeof VariationsResponseSchema>;

// Right to work -------------------------------------------------------------------------------

export const RtwDocumentTypeSchema = z.enum([
  'uk_passport',
  'irish_passport',
  'birth_certificate_ni',
  'share_code',
  'brp',
  'visa',
  'other',
]);
export type RtwDocumentType = z.infer<typeof RtwDocumentTypeSchema>;
export const RTW_DOCUMENT_LABELS: Record<RtwDocumentType, string> = {
  uk_passport: 'UK passport',
  irish_passport: 'Irish passport',
  birth_certificate_ni: 'Birth certificate + NI number',
  share_code: 'Home Office share code',
  brp: 'Biometric residence permit',
  visa: 'Visa / time-limited permission',
  other: 'Other',
};

export const RtwCheckSchema = z.object({
  id: z.uuid(),
  person: PersonRefSchema.nullable(),
  hireId: z.uuid().nullable(),
  site: SiteRefSchema.nullable(),
  checkedOn: IsoDate,
  checkedBy: PersonRefSchema.nullable(),
  documentType: RtwDocumentTypeSchema,
  expiresOn: IsoDate.nullable(),
  note: z.string().nullable(),
  status: z.enum(['no_expiry', 'ok', 'expiring', 'expired']),
});
export type RtwCheck = z.infer<typeof RtwCheckSchema>;

/** A check recorded on a hire (before the person exists). */
export const RtwHireWriteSchema = z.object({
  checkedOn: IsoDate,
  checkedById: z.uuid().nullish(),
  documentType: RtwDocumentTypeSchema,
  expiresOn: IsoDate.nullish(),
  note: OptText(500),
});
export type RtwHireWrite = z.infer<typeof RtwHireWriteSchema>;

export const RtwWriteSchema = RtwHireWriteSchema.extend({
  personId: z.uuid().nullish(),
  hireId: z.uuid().nullish(),
}).refine((v) => !!v.personId !== !!v.hireId, { message: 'Give a person or a hire.' });
export type RtwWrite = z.infer<typeof RtwWriteSchema>;

export const RtwResponseSchema = z.object({ checks: z.array(RtwCheckSchema) });

// Hires ---------------------------------------------------------------------------------------

export const HireStatusSchema = z.enum(['approving', 'details', 'live', 'rejected', 'cancelled']);
export type HireStatus = z.infer<typeof HireStatusSchema>;
export const HireReasonSchema = z.enum(['replacement', 'new_role', 'temp_cover']);
export type HireReason = z.infer<typeof HireReasonSchema>;
export const HIRE_REASON_LABELS: Record<HireReason, string> = {
  replacement: 'Replacing a leaver',
  new_role: 'New role',
  temp_cover: 'Temporary cover',
};

export const HireSummarySchema = z.object({
  id: z.uuid(),
  ref: z.string(),
  status: HireStatusSchema,
  site: SiteRefSchema,
  jobTitle: z.string(),
  reason: HireReasonSchema,
  proposedStartOn: IsoDate.nullable(),
  startOn: IsoDate.nullable(),
  candidateName: z.string().nullable(),
  raisedBy: PersonRefSchema.nullable(),
  raisedAt: Instant,
  approval: ApprovalBriefSchema,
  person: PersonRefSchema.nullable(),
});
export type HireSummary = z.infer<typeof HireSummarySchema>;

export const HireDetailSchema = HireSummarySchema.extend({
  jobRole: z.string().nullable(),
  replacing: PersonRefSchema.nullable(),
  contractHours: z.number().nullable(),
  contractType: ContractTypeSchema.nullable(),
  holidayEntitlementDays: z.number().nullable(),
  notes: z.string().nullable(),
  givenName: z.string().nullable(),
  familyName: z.string().nullable(),
  preferredName: z.string().nullable(),
  loginEmail: z.string().nullable(),
  staffNumber: z.string().nullable(),
  pay: PaySchema.nullable(),
  payHidden: z.boolean(),
  approvals: ApprovalProgressSchema,
  checklist: ChecklistSchema.nullable(),
  induction: ChecklistSchema.nullable(),
  rtw: RtwCheckSchema.nullable(),
  goLiveBlockers: z.array(z.string()),
  can: z.object({
    editDetails: z.boolean(),
    goLive: z.boolean(),
    cancel: z.boolean(),
    recordRtw: z.boolean(),
  }),
});
export type HireDetail = z.infer<typeof HireDetailSchema>;

export const HireCreateSchema = z.object({
  siteId: z.uuid(),
  jobTitle: Text(100).min(1),
  jobRole: z.string().max(40).nullish(),
  reason: HireReasonSchema,
  replacingPersonId: z.uuid().nullish(),
  replacingLeaverId: z.uuid().nullish(),
  proposedStartOn: IsoDate.nullish(),
  contractHours: z.number().min(0).max(80).nullish(),
  contractType: ContractTypeSchema.nullish(),
  notes: OptText(2000),
});
export type HireCreate = z.infer<typeof HireCreateSchema>;

export const HireDetailsWriteSchema = z.object({
  givenName: Text(100).min(1),
  familyName: Text(100).min(1),
  preferredName: OptText(100),
  loginEmail: z.email().max(255).nullish(),
  staffNumber: OptText(32),
  startOn: IsoDate,
  jobTitle: OptText(100),
  jobRole: z.string().max(40).nullish(),
  contractHours: z.number().min(0).max(80).nullish(),
  contractType: ContractTypeSchema.nullish(),
  holidayEntitlementDays: z.number().min(0).max(60).nullish(),
  pay: PayWriteSchema.nullish(),
});
export type HireDetailsWrite = z.infer<typeof HireDetailsWriteSchema>;

export const HireCloseSchema = z.object({ reason: Text(500).min(1) });

export const GoLiveResultSchema = z.object({
  personId: z.uuid(),
  accountCreated: z.boolean(),
  inviteSent: z.boolean(),
});
export type GoLiveResult = z.infer<typeof GoLiveResultSchema>;

export const HiresResponseSchema = z.object({ hires: z.array(HireSummarySchema) });
export type HiresResponse = z.infer<typeof HiresResponseSchema>;

// Leavers -------------------------------------------------------------------------------------

export const LeaverReasonSchema = z.enum([
  'resigned',
  'dismissed',
  'end_of_contract',
  'redundancy',
  'retired',
  'probation',
  'other',
]);
export type LeaverReason = z.infer<typeof LeaverReasonSchema>;
export const LEAVER_REASON_LABELS: Record<LeaverReason, string> = {
  resigned: 'Resigned',
  dismissed: 'Dismissed',
  end_of_contract: 'End of contract',
  redundancy: 'Redundancy',
  retired: 'Retired',
  probation: 'Failed probation',
  other: 'Other',
};

export const LeaverSummarySchema = z.object({
  id: z.uuid(),
  person: PersonRefSchema,
  site: SiteRefSchema.nullable(),
  noticeGivenOn: IsoDate.nullable(),
  leaveOn: IsoDate,
  reasonCategory: LeaverReasonSchema,
  status: z.enum(['open', 'closed']),
  replacement: z.enum(['none', 'requested', 'approved', 'rejected']),
  tasksDone: z.number().int(),
  tasksTotal: z.number().int(),
});
export type LeaverSummary = z.infer<typeof LeaverSummarySchema>;

export const LeaverDetailSchema = LeaverSummarySchema.extend({
  reason: z.string().nullable(),
  competitor: z.string().nullable(),
  gardenLeave: z.boolean(),
  eligibleRehire: z.boolean().nullable(),
  replacementNote: z.string().nullable(),
  approvals: ApprovalProgressSchema.nullable(),
  checklist: ChecklistSchema.nullable(),
  replacementHire: z.object({ id: z.uuid(), ref: z.string(), status: HireStatusSchema }).nullable(),
  can: z.object({
    requestReplacement: z.boolean(),
    raiseHire: z.boolean(),
    close: z.boolean(),
  }),
});
export type LeaverDetail = z.infer<typeof LeaverDetailSchema>;

export const LeaverCreateSchema = z.object({
  personId: z.uuid(),
  leaveOn: IsoDate,
  noticeGivenOn: IsoDate.nullish(),
  reasonCategory: LeaverReasonSchema,
  reason: OptText(500),
  competitor: OptText(200),
  gardenLeave: z.boolean().default(false),
  eligibleRehire: z.boolean().nullish(),
  requestReplacement: z.boolean().default(false),
  replacementNote: OptText(1000),
});
export type LeaverCreate = z.infer<typeof LeaverCreateSchema>;

export const ReplacementRequestSchema = z.object({ note: OptText(1000) });

export const LeaversResponseSchema = z.object({ leavers: z.array(LeaverSummarySchema) });

// Probation and 1:1 reviews -------------------------------------------------------------------

export const ReviewSummarySchema = z.object({
  id: z.uuid(),
  person: PersonRefSchema,
  site: SiteRefSchema.nullable(),
  kind: z.enum(['probation', 'adhoc']),
  number: z.number().int().nullable(),
  title: z.string(),
  dueOn: IsoDate,
  status: z.enum(['pending', 'completed', 'cancelled']),
  heldOn: IsoDate.nullable(),
  overdue: z.boolean(),
  signed: z.boolean(),
});
export type ReviewSummary = z.infer<typeof ReviewSummarySchema>;

export const ReviewDetailSchema = ReviewSummarySchema.extend({
  reviewer: PersonRefSchema.nullable(),
  overallRating: RagSchema.nullable(),
  overallComments: z.string().nullable(),
  attitudeRating: RagSchema.nullable(),
  attitudeComments: z.string().nullable(),
  trainingRating: RagSchema.nullable(),
  trainingComments: z.string().nullable(),
  managerNotes: z.string().nullable(),
  employeeNotes: z.string().nullable(),
  areasToImprove: z.string().nullable(),
  areasOfConcern: z.string().nullable(),
  document: HrDocumentSchema.nullable(),
  can: z.object({ edit: z.boolean(), complete: z.boolean(), cancel: z.boolean() }),
});
export type ReviewDetail = z.infer<typeof ReviewDetailSchema>;

export const ReviewWriteSchema = z.object({
  heldOn: IsoDate,
  overallRating: RagSchema.nullish(),
  overallComments: OptText(4000),
  attitudeRating: RagSchema.nullish(),
  attitudeComments: OptText(4000),
  trainingRating: RagSchema.nullish(),
  trainingComments: OptText(4000),
  managerNotes: OptText(8000),
  employeeNotes: OptText(8000),
  areasToImprove: OptText(4000),
  areasOfConcern: OptText(4000),
});
export type ReviewWrite = z.infer<typeof ReviewWriteSchema>;

export const ProbationOutcomeSchema = z.enum(['taken_on', 'extended', 'let_go']);
export type ProbationOutcome = z.infer<typeof ProbationOutcomeSchema>;
export const PROBATION_OUTCOME_LABELS: Record<ProbationOutcome, string> = {
  taken_on: 'Passed: taken on',
  extended: 'Probation extended',
  let_go: 'Let go',
};

export const ProbationSummarySchema = z.object({
  person: PersonRefSchema,
  site: SiteRefSchema.nullable(),
  startsOn: IsoDate,
  endsOn: IsoDate,
  extendedTo: IsoDate.nullable(),
  outcome: ProbationOutcomeSchema.nullable(),
  decidedAt: Instant.nullable(),
  reviewsDone: z.number().int(),
  reviewsTotal: z.number().int(),
  endingSoon: z.boolean(),
});
export type ProbationSummary = z.infer<typeof ProbationSummarySchema>;

export const ProbationOutcomeWriteSchema = z
  .object({
    outcome: ProbationOutcomeSchema,
    extendedTo: IsoDate.nullish(),
    note: OptText(1000),
  })
  .refine((v) => v.outcome !== 'extended' || !!v.extendedTo, {
    message: 'Give the new end date for an extension.',
  });
export type ProbationOutcomeWrite = z.infer<typeof ProbationOutcomeWriteSchema>;

export const ProbationResponseSchema = z.object({
  reviews: z.array(ReviewSummarySchema),
  probations: z.array(ProbationSummarySchema),
  canDecide: z.boolean(),
});
export type ProbationResponse = z.infer<typeof ProbationResponseSchema>;

// Cases ---------------------------------------------------------------------------------------

export const HrCaseTypeSchema = z.enum(['disciplinary', 'grievance', 'capability']);
export type HrCaseType = z.infer<typeof HrCaseTypeSchema>;
export const HrCaseSchema = z.object({
  id: z.uuid(),
  person: PersonRefSchema,
  type: HrCaseTypeSchema,
  openedOn: IsoDate,
  closedOn: IsoDate.nullable(),
  summary: z.string().nullable(),
  outcome: z.string().nullable(),
  files: z.array(FileSummarySchema),
  createdBy: PersonRefSchema.nullable(),
  createdAt: Instant,
});
export type HrCase = z.infer<typeof HrCaseSchema>;
export const HrCaseWriteSchema = z.object({
  personId: z.uuid(),
  type: HrCaseTypeSchema,
  openedOn: IsoDate,
  closedOn: IsoDate.nullish(),
  summary: OptText(8000),
  outcome: OptText(500),
});
export type HrCaseWrite = z.infer<typeof HrCaseWriteSchema>;
export const HrCasesResponseSchema = z.object({ cases: z.array(HrCaseSchema) });

// Training ------------------------------------------------------------------------------------

export const CourseSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  validityMonths: z.number().int().nullable(),
  active: z.boolean(),
  jobRoles: z.array(z.string()),
});
export type Course = z.infer<typeof CourseSchema>;
export const CourseWriteSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z0-9_-]{2,40}$/i),
  name: Text(150).min(1),
  validityMonths: z.number().int().min(1).max(120).nullish(),
  active: z.boolean().default(true),
  jobRoles: z.array(z.string().max(40)).max(20),
});
export type CourseWrite = z.infer<typeof CourseWriteSchema>;

export const TrainingStatusSchema = z.enum(['valid', 'expiring', 'expired', 'missing']);
export type TrainingStatus = z.infer<typeof TrainingStatusSchema>;

export const TrainingRecordSchema = z.object({
  id: z.uuid(),
  person: PersonRefSchema,
  course: z.object({ id: z.uuid(), name: z.string() }),
  completedOn: IsoDate,
  expiresOn: IsoDate.nullable(),
  status: TrainingStatusSchema,
  certificate: FileSummarySchema.nullable(),
  note: z.string().nullable(),
  recordedBy: PersonRefSchema.nullable(),
});
export type TrainingRecord = z.infer<typeof TrainingRecordSchema>;

export const TrainingComplianceSchema = z.object({
  courses: z.array(CourseSchema),
  sites: z.array(
    z.object({
      site: SiteRefSchema,
      people: z.number().int(),
      required: z.number().int(),
      valid: z.number().int(),
      expiring: z.number().int(),
      expired: z.number().int(),
      missing: z.number().int(),
      pct: z.number().nullable(),
    }),
  ),
  /** Per-person detail for one site (`?siteId=`). */
  people: z.array(
    z.object({
      person: PersonRefSchema,
      jobRole: z.string().nullable(),
      courses: z.array(
        z.object({
          courseId: z.uuid(),
          status: TrainingStatusSchema,
          expiresOn: IsoDate.nullable(),
        }),
      ),
    }),
  ),
  canManage: z.boolean(),
});
export type TrainingCompliance = z.infer<typeof TrainingComplianceSchema>;

export const PersonTrainingSchema = z.object({
  jobRole: z.string().nullable(),
  required: z.array(
    z.object({
      course: CourseSchema,
      status: TrainingStatusSchema,
      expiresOn: IsoDate.nullable(),
    }),
  ),
  records: z.array(TrainingRecordSchema),
  canRecord: z.boolean(),
});
export type PersonTraining = z.infer<typeof PersonTrainingSchema>;

// Statutory leave paperwork (D45) ---------------------------------------------------------------

export const StatutoryTypeSchema = z.enum([
  'maternity',
  'paternity',
  'adoption',
  'shared_parental',
]);
export type StatutoryType = z.infer<typeof StatutoryTypeSchema>;
export const StatutoryCaseSchema = z.object({
  id: z.uuid(),
  absenceId: z.uuid(),
  person: PersonRefSchema,
  type: StatutoryTypeSchema,
  absenceStart: IsoDate,
  absenceEnd: IsoDate.nullable(),
  expectedOn: IsoDate.nullable(),
  matb1ReceivedOn: IsoDate.nullable(),
  noticeReceivedOn: IsoDate.nullable(),
  returnOn: IsoDate.nullable(),
  notes: z.string().nullable(),
  kitDays: z.array(z.object({ day: IsoDate, note: z.string().nullable() })),
  files: z.array(FileSummarySchema),
});
export type StatutoryCase = z.infer<typeof StatutoryCaseSchema>;
export const StatutoryWriteSchema = z.object({
  expectedOn: IsoDate.nullish(),
  matb1ReceivedOn: IsoDate.nullish(),
  noticeReceivedOn: IsoDate.nullish(),
  returnOn: IsoDate.nullish(),
  notes: OptText(4000),
  kitDays: z
    .array(z.object({ day: IsoDate, note: OptText(300) }))
    .max(40)
    .optional(),
});
export type StatutoryWrite = z.infer<typeof StatutoryWriteSchema>;
export const StatutoryResponseSchema = z.object({
  cases: z.array(StatutoryCaseSchema),
  absencesWithoutCase: z.array(
    z.object({
      absenceId: z.uuid(),
      person: PersonRefSchema,
      type: StatutoryTypeSchema,
      startDate: IsoDate,
      endDate: IsoDate.nullable(),
    }),
  ),
});
export type StatutoryResponse = z.infer<typeof StatutoryResponseSchema>;

// Settings, job roles, templates ---------------------------------------------------------------

export const JobRoleSchema = z.object({ code: z.string(), name: z.string(), active: z.boolean() });
export type JobRole = z.infer<typeof JobRoleSchema>;

export const HrSettingsSchema = z.object({
  retentionYears: z.number().int(),
  paySeatCodes: z.array(z.string()),
  probationMonths: z.number().int(),
  reviewDays: z.array(z.number().int()),
  rtwReminderDays: z.array(z.number().int()),
  trainingReminderDays: z.array(z.number().int()),
  digestEnabled: z.boolean(),
  jobRoles: z.array(JobRoleSchema),
  updatedAt: Instant,
});
export type HrSettings = z.infer<typeof HrSettingsSchema>;

const DayList = z.array(z.number().int().min(0).max(365)).max(6);
export const HrSettingsPatchSchema = z.object({
  retentionYears: z.number().int().min(1).max(20).optional(),
  paySeatCodes: z.array(z.string().max(60)).max(10).optional(),
  probationMonths: z.number().int().min(1).max(12).optional(),
  reviewDays: z.array(z.number().int().min(1).max(365)).min(1).max(6).optional(),
  rtwReminderDays: DayList.optional(),
  trainingReminderDays: DayList.optional(),
  digestEnabled: z.boolean().optional(),
  jobRoles: z
    .array(
      z.object({
        code: z
          .string()
          .trim()
          .regex(/^[a-z0-9_]{2,40}$/),
        name: Text(100).min(1),
        active: z.boolean(),
      }),
    )
    .max(40)
    .optional(),
});
export type HrSettingsPatch = z.infer<typeof HrSettingsPatchSchema>;

export const TemplateItemSchema = z.object({
  id: z.uuid().optional(),
  category: Text(120).min(1),
  subcategory: OptText(120),
  title: Text(300).min(1),
  ownerKind: OwnerKindSchema,
  ownerRef: z.string().max(60).nullish(),
  anchor: AnchorSchema,
  dueOffsetDays: z.number().int().min(-365).max(365),
  evidence: EvidenceSchema,
  day1: z.boolean().default(false),
});
export type TemplateItem = z.infer<typeof TemplateItemSchema>;

export const TemplateSummarySchema = z.object({
  id: z.uuid(),
  flow: ChecklistFlowSchema,
  jobRole: z.string().nullable(),
  name: z.string(),
  version: z.number().int(),
  active: z.boolean(),
  items: z.number().int(),
  imported: z.boolean(),
});
export type TemplateSummary = z.infer<typeof TemplateSummarySchema>;
export const TemplateDetailSchema = TemplateSummarySchema.extend({
  itemList: z.array(TemplateItemSchema.extend({ id: z.uuid() })),
});
export type TemplateDetail = z.infer<typeof TemplateDetailSchema>;
export const TemplateWriteSchema = z.object({
  flow: ChecklistFlowSchema,
  jobRole: z.string().max(40).nullish(),
  name: Text(150).min(1),
  active: z.boolean(),
  items: z.array(TemplateItemSchema).min(1).max(200),
});
export type TemplateWrite = z.infer<typeof TemplateWriteSchema>;
export const TemplatesResponseSchema = z.object({ templates: z.array(TemplateSummarySchema) });

// Person › Employment / Documents ---------------------------------------------------------------

export const EmploymentResponseSchema = z.object({
  personId: z.uuid(),
  canSeePay: z.boolean(),
  canManage: z.boolean(),
  canPropose: z.boolean(),
  current: TermsViewSchema.nullable(),
  history: z.array(TermsViewSchema),
  probation: ProbationSummarySchema.nullable(),
  variations: z.array(VariationSummarySchema),
  leaver: LeaverSummarySchema.nullable(),
  rtw: z.array(RtwCheckSchema),
  checklists: z.array(ChecklistSchema.omit({ tasks: true })),
});
export type EmploymentResponse = z.infer<typeof EmploymentResponseSchema>;

export const PersonDocumentsSchema = z.object({
  documents: z.array(HrDocumentSchema),
  reviews: z.array(ReviewSummarySchema),
  statutory: z.array(StatutoryCaseSchema),
});
export type PersonDocuments = z.infer<typeof PersonDocumentsSchema>;

/** HR › overview counts for the lifecycle tabs (scoped). */
export const HrLifecycleCountsSchema = z.object({
  hiresInFlight: z.number().int(),
  approvalsWaitingOnMe: z.number().int(),
  variationsPending: z.number().int(),
  leaversOpen: z.number().int(),
  reviewsDue: z.number().int(),
  myTasksDue: z.number().int(),
});
export type HrLifecycleCounts = z.infer<typeof HrLifecycleCountsSchema>;

/** People pickers in HR forms (in the caller's scope). */
export const HrPeopleSearchResponseSchema = z.object({
  people: z.array(
    PersonRefSchema.extend({ site: SiteRefSchema.nullable(), jobTitle: z.string().nullable() }),
  ),
});
export type HrPeopleSearchResponse = z.infer<typeof HrPeopleSearchResponseSchema>;

/** What the HR lifecycle screens may offer the viewer. */
export const HrMetaSchema = z.object({
  jobRoles: z.array(JobRoleSchema),
  /** Sites the viewer may raise hires / open leavers for. */
  requestSites: z.array(SiteRefSchema),
  can: z.object({
    request: z.boolean(),
    view: z.boolean(),
    manage: z.boolean(),
    settings: z.boolean(),
    cases: z.boolean(),
    trainingView: z.boolean(),
    trainingManage: z.boolean(),
    pay: z.boolean(),
  }),
});
export type HrMeta = z.infer<typeof HrMetaSchema>;

export const HrCreatedSchema = z.object({ id: z.uuid() });
